/**
 * Recover from stale chunk references after a deploy.
 *
 * Each build content-hashes its JS chunks, and a deploy replaces them. A client
 * whose app was loaded *before* the deploy still holds the old route manifest,
 * so a lazy route/component import resolves to a hash the new build removed →
 * the chunk 404s (served with an empty MIME type) → the dynamic import throws,
 * which cascades into a blank page (e.g. `can't access property "h1"` when the
 * route component never resolves).
 *
 * Nuxt emits `app:chunkError` for exactly this. Reload the app so the browser
 * fetches fresh HTML pointing at the current chunks. A short sessionStorage
 * guard prevents a reload loop if the chunk is genuinely gone (not just stale).
 */
export default defineNuxtPlugin((nuxtApp) => {
  const GUARD_KEY = "chunk-reload:last";
  const LOOP_WINDOW_MS = 10_000;

  nuxtApp.hook("app:chunkError", ({ error }) => {
    let last = 0;
    try {
      last = Number(sessionStorage.getItem(GUARD_KEY) ?? 0);
    } catch {
      // sessionStorage unavailable — fall through and attempt one reload.
    }
    const now = Date.now();
    if (now - last < LOOP_WINDOW_MS) {
      // Already reloaded once and the chunk is still missing — don't loop;
      // surface the error instead of hammering the server.
      console.error("[chunk-reload] reload already attempted, not looping", error);
      return;
    }
    try {
      sessionStorage.setItem(GUARD_KEY, String(now));
    } catch {
      // ignore — guard is best-effort.
    }
    // Reload the current location with state restored. Fresh HTML references the
    // current chunks, so the navigation the user attempted works on retry.
    reloadNuxtApp({ persistState: true });
  });
});
